"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { graphql } from "gql.tada";
import { ChevronLeftIcon, PenBoxIcon, XIcon } from "lucide-react";
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AiChatUiContext,
  type AiChatUiContextType,
  type ChatLinkComponentProps,
} from "@/lib/ai/ai-chat-ui-context";
import { type AnalysisTarget } from "@/lib/ai/analysis-target";
import {
  readStoredChatModel,
  useStoredAutoApproveToolConfirmations,
  writeStoredAutoApproveToolConfirmations,
  writeStoredChatModel,
} from "@/lib/ai/chat-preferences";
import { agentChatFragment } from "@/lib/ai/fragment";
import {
  isRestrictedHighTierModel,
  resolveAiModelAccess,
} from "@/lib/ai/model-access";
import { DEFAULT_MODEL_ID, type ModelId } from "@/lib/ai/models";
import { convertAgentChat } from "@/lib/ai/transform";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useAnalysisTargetSelectionState } from "@/lib/ai/use-analysis-target-selection-state";
import { VisualizationHeaderAction } from "@/lib/ai/visualization/client/header-toggle-action";
import {
  readStoredVisualizationEnabled,
  writeStoredVisualizationEnabled,
} from "@/lib/ai/visualization/client/ui-context";
import { ChatList } from "../ai/chat-list/chat-list";
import { ChatThread } from "../ai/thread/chat-thread";
import {
  type AiSidePanelViewMode,
  BLANK_DRAFT_AI_SIDE_PANEL_STATE,
  clearStoredAiSidePanelState,
  EMPTY_AI_SIDE_PANEL_STATE,
  readStoredAiSidePanelState,
  writeStoredAiSidePanelState,
} from "./ai-side-panel-state";
import { AiSidePanelThreadSurface } from "./ai-side-panel-thread-surface";
import { useAiSidePanelVisibility } from "./ai-side-panel-visibility";

const ChatThreadQuery = graphql(
  `query ChatThreadClientSide($id: ID!) {
    viewer { id name picture }
    node(id: $id) {
      __typename
      ... on AgentChat {
        id
        ...AgentChat
        createdBy { id name picture }
      }
    }
  }`,
  [agentChatFragment],
);

type AiSidePanelContextType = {
  viewMode: AiSidePanelViewMode;
  goToHistory: () => void;
  goToThread: () => void;
  isEnabled: boolean;

  // パネル内部で共有するチャット状態。
  workspaceId: string;
  lang: string;
  chatId: string | null;
  selectChat: (id: string) => void;
  clearChatSelection: () => void;
  openDraftWithMessages: (messages: AgentUIMessage[]) => void;
  currentModel: ModelId;
  selectModel: (modelId: ModelId) => void;
  aiModelAccess: ReturnType<typeof resolveAiModelAccess>;
  analysisTargets: AnalysisTarget[];
  pageAnalysisTarget: AnalysisTarget | null;
  manualAnalysisTargets: AnalysisTarget[];
  setAnalysisTargets: (targets: AnalysisTarget[]) => void;
  addAnalysisTarget: (target: AnalysisTarget) => void;
  removeAnalysisTarget: (target: AnalysisTarget) => void;
  removeLastAnalysisTarget: () => void;
  clearAnalysisTargets: () => void;
  setPageAnalysisTarget: (target: AnalysisTarget | null) => void;
  orderKey: "CREATED_AT" | "UPDATED_AT";
  orderDirection: "ASC" | "DESC";
  setOrder: (
    key: "CREATED_AT" | "UPDATED_AT",
    direction: "ASC" | "DESC",
  ) => void;
  toggleOrderDirection: () => void;
  includesShared: boolean;
  setIncludesShared: (include: boolean) => void;
  isVisualizationEnabled: boolean;
  setVisualizationEnabled: (enabled: boolean) => void;
  autoApproveToolConfirmations: boolean;
  setAutoApproveToolConfirmations: (enabled: boolean) => void;

  // query 結果から組み立てた表示内容。
  panelContentRaw: ReactNode;
};

const AiSidePanelContext = createContext<AiSidePanelContextType | null>(null);

export function useAiSidePanel() {
  const context = useContext(AiSidePanelContext);
  if (!context) {
    throw new Error(
      "useAiSidePanel must be used within an AiSidePanelProvider",
    );
  }
  return context;
}

type AiSidePanelProviderProps = {
  children: ReactNode;
  workspaceId: string;
  lang: string;
  isEnabled: boolean;
  contractOptions?: readonly string[];
  restrictedModelIds: readonly ModelId[];
};

export function AiSidePanelProvider({
  children,
  workspaceId,
  lang,
  isEnabled,
  contractOptions = [],
  restrictedModelIds,
}: AiSidePanelProviderProps) {
  const { open, isVisible } = useAiSidePanelVisibility();

  const restoredSidePanelState = useMemo(
    () => readStoredAiSidePanelState(workspaceId),
    [workspaceId],
  );
  const [viewMode, setViewMode] = useState<AiSidePanelViewMode>(
    restoredSidePanelState?.viewMode ?? "thread",
  );

  const [chatId, setChatId] = useState<string | null>(
    restoredSidePanelState?.chatId ?? null,
  );
  const [draftInitialMessages, setDraftInitialMessages] = useState<
    AgentUIMessage[]
  >([]);
  const [orderKey, setOrderKey] = useState<"CREATED_AT" | "UPDATED_AT">(
    "UPDATED_AT",
  );
  const [orderDirection, setOrderDirection] = useState<"ASC" | "DESC">("DESC");
  const [includesShared, setIncludesShared] = useState(true);
  const analysisScopeStorageKey = `ai-side-panel-analysis-scope:${workspaceId}`;
  const {
    analysisTargets,
    pageAnalysisTarget,
    manualAnalysisTargets,
    setAnalysisTargets,
    addAnalysisTarget,
    removeAnalysisTarget,
    removeLastAnalysisTarget,
    clearAnalysisTargets,
    setPageAnalysisTarget,
  } = useAnalysisTargetSelectionState({
    storageKey: analysisScopeStorageKey,
  });

  const aiModelAccess = useMemo(
    () => resolveAiModelAccess(contractOptions, true, restrictedModelIds),
    [contractOptions, restrictedModelIds],
  );
  const resolveSelectableModel = useCallback(
    (modelId: ModelId | null) => {
      if (!modelId) return null;
      if (isRestrictedHighTierModel(modelId, aiModelAccess)) {
        return DEFAULT_MODEL_ID;
      }
      return modelId;
    },
    [aiModelAccess],
  );
  const [currentModelState, setCurrentModelState] = useState<ModelId>(() => {
    return resolveSelectableModel(readStoredChatModel()) ?? DEFAULT_MODEL_ID;
  });
  const currentModel =
    resolveSelectableModel(currentModelState) ?? DEFAULT_MODEL_ID;
  const [isVisualizationEnabled, setVisualizationEnabledState] = useState(
    () => {
      return readStoredVisualizationEnabled() ?? true;
    },
  );
  const autoApproveToolConfirmations = useStoredAutoApproveToolConfirmations();
  const effectiveViewMode = viewMode;
  const effectiveChatId = chatId;
  const effectiveDraftInitialMessages = draftInitialMessages;

  const persistHistoryFallback = useCallback(() => {
    writeStoredAiSidePanelState(workspaceId, EMPTY_AI_SIDE_PANEL_STATE);
  }, [workspaceId]);
  const persistBlankDraftThread = useCallback(() => {
    writeStoredAiSidePanelState(workspaceId, BLANK_DRAFT_AI_SIDE_PANEL_STATE);
  }, [workspaceId]);

  /**
   * side panel は hidden の間も mounted のままになりうるので、shared preference は
   * mount 時だけでなく visible 復帰時にも storage を再読込する。
   */
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const storedModel = resolveSelectableModel(readStoredChatModel());
    const storedVisualizationEnabled = readStoredVisualizationEnabled();
    if (storedModel && storedModel !== currentModel) {
      setCurrentModelState(storedModel);
    }
    if (
      storedVisualizationEnabled !== null &&
      storedVisualizationEnabled !== isVisualizationEnabled
    ) {
      setVisualizationEnabledState(storedVisualizationEnabled);
    }
  }, [currentModel, isVisualizationEnabled, isVisible, resolveSelectableModel]);

  const selectChat = useCallback(
    (id: string) => {
      setChatId(id);
      setDraftInitialMessages([]);
      setViewMode("thread");
      writeStoredAiSidePanelState(workspaceId, {
        chatId: id,
        viewMode: "thread",
      });
      open();
    },
    [open, workspaceId],
  );

  const clearChatSelection = useCallback(() => {
    setChatId(null);
    setDraftInitialMessages([]);
    setViewMode("thread"); // 新規チャット用の draft 表示へ戻す。
    persistBlankDraftThread();
  }, [persistBlankDraftThread]);

  const openDraftWithMessages = useCallback(
    (messages: AgentUIMessage[]) => {
      setChatId(null);
      setDraftInitialMessages(messages);
      setViewMode("thread");
      persistHistoryFallback();
      open();
    },
    [open, persistHistoryFallback],
  );

  const goToHistory = useCallback(() => {
    setViewMode("history");
    writeStoredAiSidePanelState(workspaceId, {
      chatId,
      viewMode: "history",
    });
  }, [chatId, workspaceId]);

  const goToThread = useCallback(() => {
    setViewMode("thread");
    if (chatId) {
      writeStoredAiSidePanelState(workspaceId, {
        chatId,
        viewMode: "thread",
      });
      return;
    }

    // 空の draft は再構築できるが、初期メッセージ付き draft はメモリにしかないため
    // 再読込時は履歴へ戻す。
    if (draftInitialMessages.length === 0) {
      persistBlankDraftThread();
      return;
    }

    persistHistoryFallback();
  }, [
    chatId,
    draftInitialMessages.length,
    persistBlankDraftThread,
    persistHistoryFallback,
    workspaceId,
  ]);

  const selectModel = useCallback(
    (modelId: ModelId) => {
      const safeModel = resolveSelectableModel(modelId) ?? DEFAULT_MODEL_ID;
      setCurrentModelState(safeModel);
      writeStoredChatModel(safeModel);
    },
    [resolveSelectableModel],
  );

  /**
   * side panel には URL query がないため、page と共有する preference は storage を
   * 正本にする。これで page / sidebar のどちらから切り替えても次回初期値が揃う。
   */
  const setVisualizationEnabled = useCallback((enabled: boolean) => {
    setVisualizationEnabledState(enabled);
    writeStoredVisualizationEnabled(enabled);
  }, []);

  const setAutoApproveToolConfirmations = useCallback((enabled: boolean) => {
    writeStoredAutoApproveToolConfirmations(enabled);
  }, []);

  const setOrder = useCallback(
    (key: "CREATED_AT" | "UPDATED_AT", direction: "ASC" | "DESC") => {
      setOrderKey(key);
      setOrderDirection(direction);
    },
    [],
  );

  const toggleOrderDirection = useCallback(() => {
    setOrderDirection((prev) => (prev === "ASC" ? "DESC" : "ASC"));
  }, []);

  const setIncludesSharedHandler = useCallback((include: boolean) => {
    setIncludesShared(include);
  }, []);

  const [{ data, fetching, error, operation, stale }] = useQuery({
    query: ChatThreadQuery,
    variables: { id: effectiveChatId ?? "" },
    // 選択状態は保持しつつ、非表示中の取得は再表示まで遅らせる。
    pause: !isVisible || !effectiveChatId,
  });

  let panelContentRaw: ReactNode;

  const hasActiveChatSelection = Boolean(effectiveChatId);
  const isMatchingOperation =
    hasActiveChatSelection && operation?.variables?.id === effectiveChatId;
  const isCurrentChatQuerySettling =
    hasActiveChatSelection && (fetching || stale || !isMatchingOperation);
  /**
   * workspace 切替直後の一描画だけ URQL に旧 query の error が残ることがある。
   * 現在の operation と一致した結果だけを扱い、古い 404 で保存済み選択を消さない。
   */
  const synchronizedChatData =
    hasActiveChatSelection && !isCurrentChatQuerySettling ? data : null;
  const synchronizedChatError =
    hasActiveChatSelection && !isCurrentChatQuerySettling ? error : null;
  const synchronizedChatNode = synchronizedChatData?.node;
  const synchronizedViewer = synchronizedChatData?.viewer;

  // 現在の chat を取得中、operation が未同期、または結果未確定なら loading とする。
  const isLoading =
    hasActiveChatSelection &&
    (isCurrentChatQuerySettling ||
      (!synchronizedChatData && !synchronizedChatError));

  // invalid/error は現在の chat と同期した結果だけを表示する。
  const isInvalidChat =
    !isLoading &&
    !synchronizedChatError &&
    Boolean(synchronizedChatData) &&
    (!synchronizedChatNode || synchronizedChatNode.__typename !== "AgentChat");
  const hasActiveChatError = Boolean(synchronizedChatError);
  const isMissingStoredChatError =
    hasActiveChatError &&
    /404|not found/i.test(synchronizedChatError?.message ?? "");

  useEffect(() => {
    if (!isInvalidChat && !isMissingStoredChatError) {
      return;
    }

    clearStoredAiSidePanelState(workspaceId);
    setChatId(null);
    setViewMode("history");
    setDraftInitialMessages([]);
  }, [isInvalidChat, isMissingStoredChatError, workspaceId]);

  if (hasActiveChatError || isInvalidChat) {
    const errorMessage = synchronizedChatError?.message || "Chat not found";
    panelContentRaw = (
      <div className="p-4 text-destructive text-sm">
        <Trans>Failed to load chat.</Trans> {errorMessage}
      </div>
    );
  } else {
    const chat =
      effectiveChatId &&
      synchronizedChatNode &&
      synchronizedChatNode.__typename === "AgentChat"
        ? convertAgentChat(synchronizedChatNode)
        : undefined;
    const isCreatedByViewer =
      synchronizedChatNode && synchronizedChatNode.__typename === "AgentChat"
        ? synchronizedChatNode.createdBy?.id === synchronizedViewer?.id
        : undefined;

    panelContentRaw = (
      <ChatThread
        workspaceId={workspaceId}
        chatId={effectiveChatId}
        initialMessages={
          effectiveChatId ? chat?.messages : effectiveDraftInitialMessages
        }
        isVisible={isVisible}
        compactStarterWhenInactive={false}
        creatorName={
          synchronizedChatNode &&
          synchronizedChatNode.__typename === "AgentChat"
            ? synchronizedChatNode.createdBy?.name
            : null
        }
        creatorPicture={
          synchronizedChatNode &&
          synchronizedChatNode.__typename === "AgentChat"
            ? synchronizedChatNode.createdBy?.picture
            : null
        }
        isCreatedByViewer={isCreatedByViewer}
        isLoading={isLoading}
        onChatCreated={selectChat}
      />
    );
  }

  const contextValue: AiSidePanelContextType = useMemo(
    () => ({
      viewMode: effectiveViewMode,
      goToHistory,
      goToThread,
      isEnabled,
      workspaceId,
      lang,
      chatId: effectiveChatId,
      selectChat,
      clearChatSelection,
      openDraftWithMessages,
      currentModel,
      selectModel,
      aiModelAccess,
      analysisTargets,
      pageAnalysisTarget,
      manualAnalysisTargets,
      setAnalysisTargets,
      addAnalysisTarget,
      removeAnalysisTarget,
      removeLastAnalysisTarget,
      clearAnalysisTargets,
      setPageAnalysisTarget,
      orderKey,
      orderDirection,
      setOrder,
      toggleOrderDirection,
      includesShared,
      setIncludesShared: setIncludesSharedHandler,
      isVisualizationEnabled,
      setVisualizationEnabled,
      autoApproveToolConfirmations,
      setAutoApproveToolConfirmations,
      panelContentRaw,
    }),
    [
      effectiveViewMode,
      goToHistory,
      goToThread,
      isEnabled,
      workspaceId,
      lang,
      effectiveChatId,
      selectChat,
      clearChatSelection,
      openDraftWithMessages,
      currentModel,
      selectModel,
      aiModelAccess,
      analysisTargets,
      pageAnalysisTarget,
      manualAnalysisTargets,
      setAnalysisTargets,
      addAnalysisTarget,
      removeAnalysisTarget,
      removeLastAnalysisTarget,
      clearAnalysisTargets,
      setPageAnalysisTarget,
      orderKey,
      orderDirection,
      setOrder,
      toggleOrderDirection,
      includesShared,
      setIncludesSharedHandler,
      isVisualizationEnabled,
      setVisualizationEnabled,
      autoApproveToolConfirmations,
      setAutoApproveToolConfirmations,
      panelContentRaw,
    ],
  );

  return (
    <AiSidePanelContext.Provider value={contextValue}>
      {children}
    </AiSidePanelContext.Provider>
  );
}

/**
 * サイドパネル本体を描画し、配下のチャットへ `AiChatUiContext` を提供する。
 * `AiSidePanelProvider` の内側に配置する。
 */
export function AiSidePanelContent() {
  const { i18n } = useLingui();
  const {
    workspaceId,
    lang,
    chatId,
    selectChat,
    clearChatSelection,
    currentModel,
    selectModel,
    aiModelAccess,
    analysisTargets,
    pageAnalysisTarget,
    manualAnalysisTargets,
    setAnalysisTargets,
    addAnalysisTarget,
    removeAnalysisTarget,
    removeLastAnalysisTarget,
    clearAnalysisTargets,
    setPageAnalysisTarget,
    orderKey,
    orderDirection,
    setOrder,
    toggleOrderDirection,
    includesShared,
    setIncludesShared,
    isVisualizationEnabled,
    setVisualizationEnabled,
    autoApproveToolConfirmations,
    setAutoApproveToolConfirmations,
    panelContentRaw,
    viewMode,
    goToHistory,
    isEnabled,
  } = useAiSidePanel();
  const { close, isVisible } = useAiSidePanelVisibility();

  const ChatLinkComponent: FC<ChatLinkComponentProps> = useCallback(
    ({ children, chatId: linkChatId, className, isSelected }) => {
      // URL 遷移を行わず、パネル内の選択状態だけを切り替える。
      return (
        <div
          className={className}
          onClick={() => selectChat(linkChatId)}
          data-selected={isSelected}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              selectChat(linkChatId);
            }
          }}
          style={{ cursor: "pointer" }}
        >
          {children}
        </div>
      );
    },
    [selectChat],
  );

  const aiContextValue: AiChatUiContextType = useMemo(
    () => ({
      workspaceId,
      lang,
      chatId,
      selectChat,
      clearChatSelection,
      currentModel,
      selectModel,
      aiModelAccess,
      analysisTargets,
      pageAnalysisTarget,
      manualAnalysisTargets,
      setAnalysisTargets,
      addAnalysisTarget,
      removeAnalysisTarget,
      removeLastAnalysisTarget,
      clearAnalysisTargets,
      setPageAnalysisTarget,
      orderKey,
      orderDirection,
      setOrder,
      toggleOrderDirection,
      includesShared,
      setIncludesShared,
      autoApproveToolConfirmations,
      setAutoApproveToolConfirmations,
      chatLinkComponent: ChatLinkComponent,
    }),
    [
      workspaceId,
      lang,
      chatId,
      selectChat,
      clearChatSelection,
      currentModel,
      selectModel,
      aiModelAccess,
      analysisTargets,
      pageAnalysisTarget,
      manualAnalysisTargets,
      setAnalysisTargets,
      addAnalysisTarget,
      removeAnalysisTarget,
      removeLastAnalysisTarget,
      clearAnalysisTargets,
      setPageAnalysisTarget,
      orderKey,
      orderDirection,
      setOrder,
      toggleOrderDirection,
      includesShared,
      setIncludesShared,
      autoApproveToolConfirmations,
      setAutoApproveToolConfirmations,
      ChatLinkComponent,
    ],
  );

  if (!isEnabled) {
    return null;
  }

  return (
    <AiChatUiContext.Provider value={aiContextValue}>
      {viewMode === "history" ? (
        <div className="h-full ">
          {isVisible ? <ChatList onClose={close} /> : null}
        </div>
      ) : (
        <AiSidePanelThreadSurface
          isVisualizationEnabled={isVisualizationEnabled}
          setVisualizationEnabled={setVisualizationEnabled}
        >
          <div className="flex h-full w-full flex-col border-l border-border/70 bg-background">
            <div className="flex h-11 items-center justify-between border-b px-1.5 sm:px-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={goToHistory}
                className="gap-1 min-w-0 px-2"
              >
                <ChevronLeftIcon className="size-4" />
                <span className="hidden sm:inline">
                  <Trans>History</Trans>
                </span>
              </Button>
              <div className="flex items-center gap-1">
                {chatId ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={clearChatSelection}
                        className="size-8"
                        aria-label={i18n._(msg`New AI Chat`)}
                      >
                        <PenBoxIcon className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <Trans>New AI Chat</Trans>
                    </TooltipContent>
                  </Tooltip>
                ) : null}
                <VisualizationHeaderAction />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={close}
                  className="size-8"
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
            </div>
            {panelContentRaw}
          </div>
        </AiSidePanelThreadSurface>
      )}
    </AiChatUiContext.Provider>
  );
}
